import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SpecialTag } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

interface SelectSpecialTagsModalProps {
  open: boolean;
  excludeTagIds: string[];
  onClose: () => void;
  onSelect: (tag: SpecialTag) => void;
}

export function SelectSpecialTagsModal({
  open,
  excludeTagIds,
  onClose,
  onSelect,
}: SelectSpecialTagsModalProps) {
  const specialTags = useReferenceDataStore((s) => s.specialTags);

  const existingTags = React.useMemo(() => {
    const excludeSet = new Set(excludeTagIds);
    return specialTags
      .filter((t) => !excludeSet.has(t.id))
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [specialTags, excludeTagIds]);

  const [selectedTag, setSelectedTag] = useState<SpecialTag | null>(null);
  const [newId, setNewId] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [idError, setIdError] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedTag(null);
      setNewId('');
      setNewDescription('');
      setIdError('');
    }
  }, [open]);

  const handlePickExisting = (tag: SpecialTag) => {
    setSelectedTag(tag);
    setNewId('');
    setNewDescription('');
    setIdError('');
  };

  const handleNewIdChange = (value: string) => {
    setNewId(value);
    setIdError('');
    if (value) setSelectedTag(null);
  };

  const canConfirm = selectedTag !== null || newId.trim() !== '';

  const handleConfirm = () => {
    if (selectedTag) {
      onSelect(selectedTag);
      return;
    }
    if (!newId.trim()) {
      setIdError('Tag name is required');
      return;
    }
    onSelect({ id: newId.trim(), description: newDescription.trim() || null });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Special Tag</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {existingTags.length > 0 && (
          <>
            <Typography variant="subtitle2">Existing Tags</Typography>
            <List disablePadding sx={{ maxHeight: 240, overflowY: 'auto' }}>
              {existingTags.map((tag) => (
                <ListItemButton
                  key={tag.id}
                  selected={selectedTag?.id === tag.id}
                  onClick={() => handlePickExisting(tag)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText primary={tag.id} secondary={tag.description ?? undefined} />
                </ListItemButton>
              ))}
            </List>
            <Divider />
          </>
        )}

        <Typography variant="subtitle2">New Tag</Typography>
        <Stack spacing={1.5}>
          <TextField
            label="Tag Name"
            value={newId}
            onChange={(e) => handleNewIdChange(e.target.value)}
            error={!!idError}
            helperText={idError}
            size="small"
            fullWidth
          />
          <TextField
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canConfirm} onClick={handleConfirm}>
          Add Tag
        </Button>
      </DialogActions>
    </Dialog>
  );
}

